import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication } from '@azure/msal-browser'
import { LazyMotion, MotionConfig, domMax } from 'motion/react'
import { Toaster } from 'sonner'
// Self-hosted fonts (D-13-02-2): Montserrat for UI/body, Source Serif 4 for headings, both
// variable, bundled by Vite into /assets so the CSP needs only font-src 'self'. The italic
// serif is the greeting's <em>.
import '@fontsource-variable/montserrat/wght.css'
import '@fontsource-variable/source-serif-4/wght.css'
import '@fontsource-variable/source-serif-4/wght-italic.css'
import { msalConfig } from './auth/authConfig'
import { clearStaleInteraction } from './auth/staleInteraction'
import AuthProvider from './auth/AuthProvider'
import UpdateBanner from './components/UpdateBanner'
import { TOASTER_STYLE } from './config/toaster'
import { offerUpdate } from './services/swUpdate'
import './index.css'
import App from './App.tsx'

// CRITICAL: Instantiate MSAL at module level, outside the React component tree.
// This is required by @azure/msal-react for correct initialization.
const msalInstance = new PublicClientApplication(msalConfig);

// A sign-in popup that died earlier can leave MSAL's per-tab "interaction in progress" flag behind, and a refresh
// never clears it (only a new tab did). Clear it before MSAL starts (ISS-240 follow-up).
try {
  if (clearStaleInteraction(window.sessionStorage, window.location, Boolean(window.opener))) {
    console.warn('[MSAL] cleared a stale interaction_in_progress flag left by an earlier sign-in');
  }
} catch {
  // sessionStorage itself can be unavailable; MSAL reports its own errors.
}

msalInstance.initialize().then(async () => {
  // Process auth code from redirect flow — after Microsoft login,
  // the browser returns here with #code= in the URL hash.
  // This must complete before rendering so the account is available.
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      msalInstance.setActiveAccount(response.account);
    }
  } catch (err) {
    // Redirect promise can fail if auth code is invalid/expired — log but continue
    console.warn('[MSAL] handleRedirectPromise failed:', err);
  }

  // Set active account from cache if available (e.g., returning user)
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {/* Motion: the lazy DOM bundle with layout support (domMax, needed for the queue's
          gap-close), `strict` so a stray full `motion.*` import fails loudly, and
          reducedMotion="user" so OS reduced motion makes every Motion change instant. */}
      <LazyMotion features={domMax} strict>
        <MotionConfig reducedMotion="user">
          <AuthProvider msalInstance={msalInstance}>
            <App />
          </AuthProvider>
          <UpdateBanner />
          <Toaster
            position="bottom-center"
            toastOptions={{ className: 'qms-toast' }}
            containerAriaLabel="Notifications"
            style={TOASTER_STYLE}
          />
        </MotionConfig>
      </LazyMotion>
    </StrictMode>,
  );
}).catch((err) => {
  // If MSAL fails to initialize, show error in the DOM so users see something
  console.error('[MSAL] Initialization failed:', err);
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;padding:2rem;">
        <div style="text-align:center;max-width:400px;">
          <h1 style="color:#7F3F96;margin-bottom:1rem;">Harmony QMS</h1>
          <p style="color:#666;margin-bottom:1rem;">Authentication failed to initialize. Please try refreshing the page.</p>
          <p style="color:#999;font-size:0.85rem;">${err instanceof Error ? err.message : String(err)}</p>
          <button onclick="location.reload()" style="margin-top:1rem;padding:0.5rem 1.5rem;background:#7F3F96;color:white;border:none;border-radius:4px;cursor:pointer;">Refresh</button>
        </div>
      </div>`;
  }
});

// Register service worker in production only
if (import.meta.env.PROD && navigator.serviceWorker) {
  navigator.serviceWorker.register('/sw.js')
    .then((registration) => {
      console.log('SW registered:', registration.scope);

      // An update that finished installing before this page loaded is already waiting.
      if (registration.waiting && navigator.serviceWorker.controller) {
        offerUpdate(registration.waiting);
      }

      // Detect service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New SW installed and there's an existing controller = this is an UPDATE.
          // Non-blocking (13-08, RESEARCH Pitfall 6): no confirm(). The UpdateBanner offers
          // "A new version is ready — Reload"; Reload posts SKIP_WAITING to this worker.
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            offerUpdate(newWorker);
          }
        });
      });
    })
    .catch((err) => console.warn('SW registration failed:', err));

  // Reload when the new service worker takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}
